import { prepareInputFields } from "./prepare-form-inputs";

/**
 *
 * @param {HTMLElement} submitButton
 *
 * Attaches a loading spinner to the submit button on
 * form submission
 */
function attachLoadingSpinner(submitButton) {
  let spinnerClassName = "p-icon--spinner u-animation--spin";
  if (submitButton.classList.contains("p-button--positive")) {
    spinnerClassName += " is-light";
  }

  const spinnerIcon = document.createElement("i");
  spinnerIcon.className = spinnerClassName;
  const buttonRect = submitButton.getBoundingClientRect();
  submitButton.style.width = buttonRect.width + "px";
  submitButton.style.height = buttonRect.height + "px";
  submitButton.classList.add("is-processing");
  submitButton.disabled = true;
  submitButton.innerText = "";
  submitButton.appendChild(spinnerIcon);
}

/**
 *
 * @param {*} fieldset
 * @param {*} checklistItem
 *
 * Disable & enable checklist visibility based on user selection
 * - When any visible checkbox is checked, it will disable the .js-checkbox-visibility__other checkboxes
 * - Can only check one __other item at a time
 * - When all visible checkboxes or any __other checkbox is unchecked, all checkboxes will be enabled
 */
function toggleCheckboxVisibility(fieldset, checklistItem) {
  const checkboxes = fieldset.querySelectorAll(".js-checkbox-visibility");
  const otherCheckboxes = fieldset.querySelectorAll(
    ".js-checkbox-visibility__other"
  );
  const isVisible = checklistItem.classList.contains("js-checkbox-visibility");

  if (checklistItem.checked) {
    if (isVisible) {
      otherCheckboxes.forEach((checkbox) => {
        checkbox.disabled = true;
      });
    } else {
      checkboxes.forEach((checkbox) => {
        checkbox.disabled = true;
      });
      otherCheckboxes.forEach((checkbox) => {
        checklistItem == checkbox ? null : (checkbox.disabled = true);
      });
    }
  } else {
    if (isVisible) {
      var uncheck = true;
      checkboxes.forEach((checkbox) => {
        checkbox.checked ? (uncheck = false) : null;
      });
      if (uncheck) {
        otherCheckboxes.forEach((checkbox) => {
          checkbox.disabled = false;
        });
      }
    } else {
      checkboxes.forEach((checkbox) => {
        checkbox.disabled = false;
      });
      otherCheckboxes.forEach((checkbox) => {
        checkbox.disabled = false;
      });
    }
  }
}

/**
 *
 * @param {*} fieldset
 * @param {*} target
 * Disables submit button for required checkboxes field
 */
function requiredCheckbox(fieldset, target) {
  /** @type {HTMLButtonElement} */
  const submitButton = document.querySelector(".js-submit-button");
  const checkboxes = fieldset.querySelectorAll("input[type='checkbox']");
  if (target.checked) {
    submitButton.disabled = false;
  } else {
    var disableSubmit = true;
    checkboxes.forEach((checkbox) => {
      checkbox.checked ? (disableSubmit = false) : null;
    });
    submitButton.disabled = disableSubmit;
  }
}

/**
 * Sets the consent info from the data layer into the consent_info cookie
 */
function setDataLayerConsentInfo() {
  /** @type {Array} */
  const dataLayer = window.dataLayer || [];
  const latestConsentUpdateElements = dataLayer
    .slice()
    .reverse()
    .filter(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        item[0] === "consent" &&
        item[1] === "update"
    )[0]?.[2];

  if (latestConsentUpdateElements) {
    document.cookie =
      "consent_info=" +
      JSON.stringify(latestConsentUpdateElements) +
      ";max-age=31536000;";
  }
}

/**
 * @param {string} name
 *
 * Returns cookie value by name
 */
function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

/**
 * Adds cookie values as hidden form fields
 */
function addCookieFieldsToForm(form) {
  const cookieFields = ["user_id", "consent_info", "utms", "_cookies_accepted"];

  cookieFields.forEach((fieldName) => {
    const cookieValue = getCookie(fieldName);
    if (cookieValue) {
      // Remove existing hidden field if it exists
      const existingField = form.querySelector(`input[name="${fieldName}"]`);
      if (existingField) {
        existingField.remove();
      }

      // Create new hidden field
      const hiddenField = document.createElement("input");
      hiddenField.type = "hidden";
      hiddenField.name = fieldName;
      hiddenField.value = cookieValue;
      form.appendChild(hiddenField);
    }
  });
}

/**
 * Removes the name attribute from inputs whose names start with an underscore
 * prefix (e.g. _radio_, _checkbox_). These fields are used internally to build
 * the comments_from_lead__c payload and must not be submitted directly.
 * Preserves _cookies_accepted, which the submit endpoint uses for consent.
 */
function stripPrefixedFieldNames(form) {
  form
    .querySelectorAll("input[name^='_']:not([name='_cookies_accepted'])")
    .forEach((input) => {
      input.removeAttribute("name");
    });
}

const forms = document.querySelectorAll("form");
forms.forEach((form) => {
  // Add event listeners to toggle checkbox visibility
  const ubuntuVersionCheckboxes = document.querySelector(
    "fieldset.js-toggle-checkbox-visibility"
  );
  ubuntuVersionCheckboxes?.addEventListener("change", function (event) {
    toggleCheckboxVisibility(ubuntuVersionCheckboxes, event.target);
  });

  /** @type {HTMLButtonElement} */
  const submitButton = form.querySelector('button[type="submit"]');
  const requiredFieldset = form.querySelectorAll(
    "fieldset.js-required-checkbox"
  );
  // By default we disable the button, until the required fields are selected
  if (requiredFieldset.length) submitButton.disabled = true;

  // Add event listeners to required fieldset
  requiredFieldset?.forEach((fieldset) => {
    fieldset.addEventListener("change", function (event) {
      requiredCheckbox(fieldset, event.target);
    });
  });

  // Exclude forms that don't need loader
  const cancelLoader = submitButton?.classList.contains("no-loader");
  if (submitButton && !cancelLoader) {
    form.addEventListener("submit", () => {
      attachLoadingSpinner(submitButton);
      setDataLayerConsentInfo();
      addCookieFieldsToForm(form);
      stripPrefixedFieldNames(form);
    });
  }

  // This block checks for the presence of 'phone number' and 'country' input fields on the page. If either input field exists, it triggers the `prepareInputFields` function to set them up. Note: In a modal form scenario, these inputs are not present at page load and thus, `prepareInputFields` is not invoked here. Instead, the function is imported and executed within `dynamic-forms.js` when the modal is opened.
  const phoneNumberInput = form.querySelector("input#phone");
  const countryInput = form.querySelector("select#country");
  if (phoneNumberInput || countryInput) {
    prepareInputFields(phoneNumberInput, countryInput);
  }
});
